<script setup lang="ts">
import { computed, ref } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryScroller> in <template>
import ClassicPortraitGalleryScroller from './ClassicPortraitGalleryScroller.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryTile> in <template>
import ClassicPortraitGalleryTile from './ClassicPortraitGalleryTile.vue';
import {
  type ClassicPortraitGalleryPlan,
  type GalleryEntry,
  galleryTileBindings,
  type MixedPortraitGalleryItem,
  type MixedPortraitGalleryKind,
  mixedItemRatio,
} from './classic-mixed-portrait-gallery';

interface Props {
  activeUuid?: string | null;
  entries: GalleryEntry[];
  generateMoreDisabled?: boolean;
  getSelectedPortraitCount: (
    item: MixedPortraitGalleryItem
  ) => number | undefined;
  plan: ClassicPortraitGalleryPlan;
  selectedUuids: string[];
  selectionOnTile?: boolean;
  showGenerateMore?: boolean;
}

interface Zone {
  entries: GalleryEntry[];
  kind: MixedPortraitGalleryKind;
  weight: number;
}

interface ScrollTarget {
  canScroll: () => boolean;
  isItemVisible: (uuid: string) => boolean | null;
  scrollToItem: (uuid: string) => boolean;
}

const props = withDefaults(defineProps<Props>(), {
  activeUuid: null,
  generateMoreDisabled: false,
  selectionOnTile: true,
  showGenerateMore: false,
});
const emit = defineEmits<{
  activate: [item: MixedPortraitGalleryItem, index: number, event: MouseEvent];
  countDecrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  countIncrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  generateMore: [];
  itemLoad: [item: MixedPortraitGalleryItem, index: number, event: Event];
  switchSlide: [uuids: string[]];
  tryAgain: [item: MixedPortraitGalleryItem];
}>();
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const { tileBindings, tileKey } = galleryTileBindings(props, emit);

const scrollers = ref<ScrollTarget[]>([]);

// One zone per kind that has items: groups above, singles below. A session
// of one kind gets the whole height.
const zones = computed<Zone[]>(() => {
  const [groupWeight, individualWeight] = props.plan.zoneWeights;
  return (
    [
      { kind: 'group', weight: groupWeight },
      { kind: 'individual', weight: individualWeight },
    ] as const
  )
    .map((zone) => ({
      ...zone,
      entries: props.entries.filter(({ item }) => item.kind === zone.kind),
    }))
    .filter((zone) => zone.entries.length > 0);
});

// A session of one kind is one lane, whatever the plan says.
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const flow = computed(() =>
  zones.value.length > 1 ? props.plan.zoneFlow : 'rows'
);

// The weights share the space between two lanes; a lone lane takes all of
// it (a lone fr track under 1 would leave the rest empty).
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const rootStyle = computed(() => {
  const tracks =
    zones.value.length > 1
      ? zones.value.map((zone) => `minmax(0, ${zone.weight}fr)`).join(' ')
      : 'minmax(0, 1fr)';
  return flow.value === 'columns'
    ? { gridTemplateColumns: tracks }
    : { gridTemplateRows: tracks };
});

// Side by side, the group column scrolls down through its wide plates and
// the singles lane scrolls across its tall cards.
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function zoneAxis(zone: Zone): 'x' | 'y' {
  return flow.value === 'columns' && zone.kind === 'group' ? 'y' : 'x';
}

// One height for every tile of a zone: the zone's height, or less when the
// widest tile would otherwise run past the peek width.
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function zoneStyle(zone: Zone): Record<string, string> {
  const widest = zone.entries.reduce(
    (ratio, { item }) => Math.max(ratio, mixedItemRatio(item)),
    0
  );
  return {
    '--cmg-zone-peek': props.plan.peek[zone.kind],
    '--cmg-zone-widest-ratio': String(widest || 1),
  };
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function isLastZone(zone: Zone): boolean {
  return zones.value.at(-1) === zone;
}

function canScroll(): boolean {
  return scrollers.value.some((scroller) => scroller.canScroll());
}

function isItemVisible(uuid: string): boolean | null {
  for (const scroller of scrollers.value) {
    const visible = scroller.isItemVisible(uuid);
    if (visible !== null) {
      return visible;
    }
  }
  return null;
}

function scrollToItem(uuid: string): boolean {
  return scrollers.value.some((scroller) => scroller.scrollToItem(uuid));
}

defineExpose({ canScroll, isItemVisible, scrollToItem });
</script>

<template>
  <div
    class="ClassicMixedPortraitGallery__zones"
    :style="rootStyle"
    data-testid="classic-mixed-gallery-zones"
    :data-zone-flow="flow"
  >
    <ClassicPortraitGalleryScroller
      v-for="zone in zones"
      :key="zone.kind"
      ref="scrollers"
      class="ClassicMixedPortraitGallery__zone"
      :style="zoneStyle(zone)"
      :data-gallery-section="zone.kind"
      :axis="zoneAxis(zone)"
      arrows
      :item-count="zone.entries.length"
      @visible-items-change="emit('switchSlide', $event)"
    >
      <div
        class="ClassicMixedPortraitGallery__zone-track"
        :class="{
          'ClassicMixedPortraitGallery__zone-track--column': zoneAxis(zone) === 'y',
        }"
      >
        <ClassicPortraitGalleryTile
          v-for="entry in zone.entries"
          :key="tileKey(entry)"
          v-bind="tileBindings(entry)"
        />
        <button
          v-if="showGenerateMore && isLastZone(zone)"
          class="ClassicMixedPortraitGallery__more-tile"
          type="button"
          data-testid="gallery-generate-more"
          :disabled="generateMoreDisabled"
          :aria-label="$t('PortraitsGroup.buttonMore')"
          @click="emit('generateMore')"
        >
          +
        </button>
      </div>
    </ClassicPortraitGalleryScroller>
  </div>
</template>

<style scoped lang="scss">
.ClassicMixedPortraitGallery__zones {
  display: grid;
  gap: var(--cmg-gap);
  min-width: 0;
  min-height: 0;
}

.ClassicMixedPortraitGallery__zone {
  min-height: 0;
}

/* The track is one row of tiles of one height: the zone's (less room for
   the selection glow), or less so that the widest tile stays within the
   peek width and the next one shows. Each tile is as wide as its ratio
   needs. Snap points at each tile's start. */
.ClassicMixedPortraitGallery__zone-track {
  --cmg-zone-inset: 0.3rem;
  --cmg-tile-max-height: min(
    calc(100cqh - 2 * var(--cmg-zone-inset)),
    calc(var(--cmg-zone-peek) / var(--cmg-zone-widest-ratio, 1))
  );

  display: flex;
  gap: var(--cmg-gap);
  align-items: center;
  justify-content: safe center;
  width: max-content;
  min-width: 100%;
  height: 100%;
  padding: var(--cmg-zone-inset);
  box-sizing: border-box;
}

/* A column stacks its tiles, each as wide as the column lets it and as
   tall as its ratio needs; the next one peeks in below. */
.ClassicMixedPortraitGallery__zone-track--column {
  --cmg-tile-max-width: calc(100cqw - 2 * var(--cmg-zone-inset));
  --cmg-tile-max-height: calc(100cqh - 2 * var(--cmg-zone-inset));

  flex-direction: column;
  width: 100%;
  min-width: 0;
  height: max-content;
  min-height: 100%;
}

/* The snap points line up with the inset, so the start is scroll 0. */
.ClassicMixedPortraitGallery__zone :deep(.ClassicMixedPortraitGallery__scroller-viewport) {
  scroll-padding-inline: var(--cmg-zone-inset, 0.3rem);
  scroll-padding-block: var(--cmg-zone-inset, 0.3rem);
}

.ClassicMixedPortraitGallery__zone-track > * {
  scroll-snap-align: start;
}

.ClassicMixedPortraitGallery__more-tile {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: max(var(--cmg-touch) * 1.6, 6rem);
  height: var(--cmg-tile-max-height);
  font-size: 3rem;
  line-height: 1;
  color: var(--Kiosk--tint-inverse-color);
  cursor: pointer;
  background: rgba(var(--Kiosk--tint-color-rgb), 0.18);
  border: 2px dashed rgba(var(--Kiosk--tint-inverse-color-rgb), 0.5);
  border-radius: var(--cmg-radius, 0.8rem);
  outline: none;
}

.ClassicMixedPortraitGallery__more-tile:disabled {
  cursor: default;
  opacity: 0.4;
}
</style>
